import { useId } from "react";

import { affiliationStanding } from "@ondo/shared";

import { Tag } from "../lib/Tag.tsx";
import { useModalDialog } from "../lib/useModalDialog.ts";
import { COUNTERPARTY_COPY } from "./counterparty.constants.ts";
import type { CounterpartyView } from "./counterparty.model.ts";

import "../lib/dialog.css";
import "./counterparty.css";

/**
 * Who you are dealing with — OND-81.
 *
 * A native `<dialog>` for `AssignAgentDialog`'s reasons: modal semantics,
 * Escape, a real focus trap, focus returned to the control that opened it.
 * Mounted even when shut, so `close()` rather than unmounting hands focus back.
 *
 * WHAT IT DRAWS AND WHAT IT DOES NOT. The registry's own flag as "Confirmed on
 * ONDO" and never "identity verified"; the firms a person holds a seat at, with
 * what the seat may do; the record the two of you share, split into current
 * and ended by the model; and what they have published to you. No search box,
 * and one sentence saying why.
 */
export function CounterpartyDialog({ view, onClose }: { view: CounterpartyView; onClose: () => void }) {
  const dialog = useModalDialog(view.kind !== "closed");
  const titleId = useId();

  return (
    <dialog
      ref={dialog}
      className="ondo-dialog counterparty"
      aria-labelledby={titleId}
      onClose={onClose}
    >
      <header className="ondo-dialog__head">
        <h2 id={titleId}>{COUNTERPARTY_COPY.title}</h2>
        <button type="button" className="ondo-dialog__close" onClick={onClose}>
          {COUNTERPARTY_COPY.close}
        </button>
      </header>

      {view.kind === "loading" && <p className="counterparty__quiet">{COUNTERPARTY_COPY.loading}</p>}

      {view.kind === "error" && (
        <p role="alert" className="counterparty__error">
          {view.message}
        </p>
      )}

      {view.kind === "open" && (
        <div className="counterparty__body">
          <section className="counterparty__identity">
            <h3 className="counterparty__name">{view.profile.displayName}</h3>
            <p className="counterparty__meta">
              {view.ondoId} · {view.profile.kindWord}
            </p>
            <p className="counterparty__meta">
              <Tag tone={view.profile.confirmed ? "verified" : "quiet"}>
                {view.profile.confirmedWord}
              </Tag>
            </p>
            <p className="counterparty__meta">{view.profile.since}</p>
          </section>

          <section>
            <h4 className="counterparty__eyebrow">{COUNTERPARTY_COPY.firms}</h4>
            {view.profile.firms.length === 0 ? (
              <p className="counterparty__quiet">{COUNTERPARTY_COPY.noFirms}</p>
            ) : (
              <ul className="counterparty__list">
                {view.profile.firms.map((firm) => (
                  <li key={firm.key}>
                    <strong>{firm.name}</strong>
                    {firm.confirmed ? ` · ${COUNTERPARTY_COPY.confirmed}` : ""}
                    <br />
                    <span className="counterparty__quiet">{firm.seat}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h4 className="counterparty__eyebrow">{COUNTERPARTY_COPY.relations}</h4>
            {view.profile.current.length === 0 && view.profile.ended.length === 0 ? (
              <p className="counterparty__quiet">{COUNTERPARTY_COPY.noRelations}</p>
            ) : (
              <>
                {view.profile.current.length > 0 && (
                  <ul className="counterparty__list" aria-label={COUNTERPARTY_COPY.current}>
                    {view.profile.current.map((line) => (
                      <li key={line.key}>
                        <Tag tone="neutral">{COUNTERPARTY_COPY.current}</Tag> {line.label} ·{" "}
                        {line.subject} · {line.standing} · {line.when}
                      </li>
                    ))}
                  </ul>
                )}
                {view.profile.ended.length > 0 && (
                  <ul className="counterparty__list" aria-label={COUNTERPARTY_COPY.ended}>
                    {view.profile.ended.map((line) => (
                      <li key={line.key} className="counterparty__ended">
                        <Tag tone="quiet">{COUNTERPARTY_COPY.ended}</Tag> {line.label} ·{" "}
                        {line.subject} · {line.standing} · {line.when}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </section>

          <section>
            <h4 className="counterparty__eyebrow">{COUNTERPARTY_COPY.affiliations}</h4>
            {view.profile.affiliations.length === 0 ? (
              <p className="counterparty__quiet">{COUNTERPARTY_COPY.noAffiliations}</p>
            ) : (
              <ul className="counterparty__list">
                {view.profile.affiliations.map((claim) => (
                  <li key={claim.claimId}>
                    <strong>{claim.subjectName}</strong>
                    {claim.title ? ` · ${claim.title}` : ""} · {affiliationStanding(claim.outcome)}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <p className="counterparty__quiet counterparty__why">{COUNTERPARTY_COPY.noDirectory}</p>
        </div>
      )}
    </dialog>
  );
}
